import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as OrdersActions from './order.actions';
import {
  IOrder,
  IResult,
  Prescription,
  IGetOrdersQuery,
  IGetOrdersDTO,
  OrderPrescriptionType,
  IFailedWorkflowRunSummary
} from '@care-portals/api-interfaces';

export const ORDERS_FEATURE_KEY = 'orders';

interface OrderEntity extends IOrder {
  rxResults?: {
    prescriptions: Prescription[];
    type: OrderPrescriptionType;
  };
  failedWorkflowRuns?: IFailedWorkflowRunSummary[];
}
export interface State extends EntityState<OrderEntity> {
  selectedId?: string | number; // which Orders record has been selected
  loading: boolean; // if the products list is loading, creating order
  loadingOrders: boolean;
  loadingOrder: boolean;
  exportingOrders: boolean;
  orderResult: IResult;
  savingOrder: boolean;
  savingOrderId: number;
  error?: string | null; // last none error (if any)
  ordersCount: number;
  filters: IGetOrdersQuery | IGetOrdersDTO;
  hasMore: boolean;
  // orders: EntityState<IOrder>;
}

export function selectOrderId(a: IOrder): string {
  return String(a.id);
}

export interface OrdersPartialState {
  readonly [ORDERS_FEATURE_KEY]: State;
}

export const OrdersAdapter: EntityAdapter<OrderEntity> =
  createEntityAdapter<OrderEntity>({
    selectId: selectOrderId
  });

export const initialState: State = OrdersAdapter.getInitialState({
  // set initial required properties
  selectedId: null,
  loading: false,
  loadingOrders: false,
  loadingOrder: false,
  exportingOrders: false,
  orderResult: { success: false },
  savingOrder: false,
  savingOrderId: null,
  error: null,
  ordersCount: null,
  filters: {
    limit: 25,
    offset: 0
  },
  hasMore: false
});

/** True if `order` belongs to the customer the current query is scoped to (or the
 *  query isn't customer-scoped at all). Prevents an order left over from a
 *  different customer's list from being spliced into this one. */
function belongsToQueryCustomer(
  order: OrderEntity,
  filters?: IGetOrdersQuery | IGetOrdersDTO
): boolean {
  const queryCustomerId = (filters as IGetOrdersDTO)?.customerId;
  if (!queryCustomerId) return true;

  const orderCustomerId =
    typeof order.customer === 'string' ? order.customer : order.customer?._id;
  return orderCustomerId === queryCustomerId;
}

/** List reloads must not drop supplement data or the actively open order. */
function mergeOrdersForListLoad(
  orders: OrderEntity[],
  state: State,
  filters?: IGetOrdersQuery | IGetOrdersDTO
): OrderEntity[] {
  const merged = orders.map(order => {
    const existing = state.entities[String(order.id)];
    if (!existing) {
      return order;
    }
    return {
      ...order,
      ...(existing.rxResults ? { rxResults: existing.rxResults } : {}),
      ...(existing.failedWorkflowRuns
        ? { failedWorkflowRuns: existing.failedWorkflowRuns }
        : {})
    };
  });

  const selectedKey =
    state.selectedId != null ? String(state.selectedId) : null;
  if (
    selectedKey &&
    !merged.some(order => String(order.id) === selectedKey)
  ) {
    const activeOrder = state.entities[selectedKey];
    if (activeOrder && belongsToQueryCustomer(activeOrder, filters)) {
      merged.push(activeOrder);
    }
  }

  return merged;
}

const ordersReducer = createReducer(
  initialState,
  on(OrdersActions.clearActiveOrder, state => ({
    ...state,
    selectedId: null
  })),
  on(OrdersActions.loadOrders, state => ({
    ...state,
    loadingOrders: true
  })),
  on(
    OrdersActions.loadOrdersSuccess,
    (state, { loadType, orders, totalCount, filters, hasMore }) => {
      const mergedOrders = mergeOrdersForListLoad(orders, state, filters);
      const shouldAppend = loadType === 'older';
      return shouldAppend
        ? OrdersAdapter.upsertMany(mergedOrders, {
            ...state,
            loadingOrders: false,
            filters,
            ordersCount: totalCount,
            hasMore
          })
        : OrdersAdapter.setAll(mergedOrders, {
            ...state,
            loadingOrders: false,
            filters,
            ordersCount: totalCount,
            hasMore
          });
    }
  ),
  on(OrdersActions.loadOrdersFailure, (state, { error }) => ({
    ...state,
    error,
    loadingOrders: false
  })),

  on(OrdersActions.loadOrder, state => ({
    ...state,
    order: null,
    loadingOrder: true,
    error: null
  })),
  on(OrdersActions.loadOrderSuccess, (state, { order }) =>
    OrdersAdapter.upsertOne(order, {
      ...state,
      selectedId: String(order.id),
      error: null,
      loadingOrder: false
    })
  ),
  on(OrdersActions.loadOrderFailure, (state, { error }) => ({
    ...state,
    selectedId: null,
    error,
    loadingOrder: false
  })),

  on(OrdersActions.retriggerOrderSuccess, (state, { order }) =>
    OrdersAdapter.upsertOne(order, {
      ...state,
      selectedId: String(order.id),
      error: null
    })
  ),
  on(OrdersActions.retriggerOrderFailure, (state, { error }) => ({
    ...state,
    error
  })),
  on(OrdersActions.retriggerPaymentSuccess, (state, { order }) =>
    OrdersAdapter.upsertOne(order, {
      ...state,
      selectedId: String(order.id),
      error: null
    })
  ),
  on(OrdersActions.retriggerPaymentFailure, (state, { error }) => ({
    ...state,
    error
  })),

  on(
    OrdersActions.loadOrderPrescriptionSuccess,
    (state, { order, rxResults, failedWorkflowRuns }) => {
      const existing = state.entities[String(order.id)] || order;

      return OrdersAdapter.upsertOne(
        {
          ...existing,
          rxResults: {
            prescriptions: rxResults.prescriptions || [],
            type: rxResults.type
          },
          failedWorkflowRuns
        },
        {
          ...state
        }
      );
    }
  ),
  on(OrdersActions.loadOrderPrescriptionFailure, (state, { error }) => ({
    ...state,
    error,
    loadingOrder: false
  })),
  on(OrdersActions.createOrder, state => ({
    ...state,
    loading: true,
    orderResult: { success: false, data: null },
    error: null
  })),
  on(OrdersActions.createOrderSuccess, (state, { order }) => ({
    ...state,
    orderResult: { success: true, data: order },
    loading: false
  })),
  on(OrdersActions.createOrderFailure, (state, { error }) => ({
    ...state,
    error: error.error ? error.error.message : error.message,
    loading: false
  })),

  on(OrdersActions.clearOrderResult, state => ({
    ...state,
    orderResult: { success: false, data: null },
    error: null
  })),

  on(OrdersActions.updateOrder, (state, { orderId }) => ({
    ...state,
    savingOrder: true,
    savingOrderId: orderId,
    error: null
  })),
  on(OrdersActions.updateOrderSuccess, (state, { update }) =>
    OrdersAdapter.updateOne(update, {
      ...state,
      savingOrder: false,
      savingOrderId: null
    })
  ),
  on(OrdersActions.updateOrderFailure, (state, { error }) => ({
    ...state,
    error,
    savingOrder: false,
    savingOrderId: null
  })),

  on(OrdersActions.switchOrderSuccess, (state, { order }) => ({
    ...state,
    selectedId: order.id
  })),

  on(
    OrdersActions.updateOrderPrescriptionSuccess,
    (state, { orderId, prescription }) => {
      const order = state.entities[orderId];
      if (!order) return state;

      const updatedPrescriptions = order.rxResults?.prescriptions?.map(p =>
        p._id === prescription._id ? prescription : p
      ) || [prescription];

      return OrdersAdapter.upsertOne(
        {
          ...order,
          rxResults: {
            prescriptions: updatedPrescriptions,
            type: order.rxResults?.type || 'fulfilled'
          }
        },
        state
      );
    }
  ),

  on(OrdersActions.chargeOrder, (state, { orderId }) => ({
    ...state,
    savingOrder: true,
    savingOrderId: Number(orderId),
    error: null
  })),

  on(OrdersActions.chargeOrderSuccess, (state, { orderId }) => ({
    ...state,
    savingOrder: false,
    savingOrderId: null,
    error: null
  })),

  on(OrdersActions.chargeOrderFailure, (state, { error }) => ({
    ...state,
    savingOrder: false,
    savingOrderId: null,
    error: error.message || 'Failed to process payment'
  })),

  on(OrdersActions.exportOrders, state => ({
    ...state,
    exportingOrders: true,
    error: null
  })),
  on(OrdersActions.exportOrdersSuccess, state => ({
    ...state,
    exportingOrders: false
  })),
  on(OrdersActions.exportOrdersFailure, (state, { error }) => ({
    ...state,
    exportingOrders: false,
    error
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return ordersReducer(state, action);
}
